import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { SingleSelectTagPicker, TagData } from "../../_base/SingleSelectTagPicker";
import { Link, makeStyles } from "@fluentui/react-components";
import { ArrowClockwise16Regular } from "@fluentui/react-icons";
import { SmartFilingManagerFluent9 } from "@src/services/SmartFiling/SmartFilingManagerFluent9";
import { Logger } from "@src/services/Logger";
import { IProjectData } from "@src/models/shared/IProjectData";
import { InvalidateCacheService } from "@src/services/NewformaApi/InvalidateCacheService";
import { ProjectsCacheKeys } from "../../../models/StorageKeys";
import { MailboxItem, OfficeWrapper } from "@src/services/OfficeWrapper";

const DEFAULT_CACHE_KEY = ProjectsCacheKeys.fileTransferCacheName;

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    container: { display: "flex" },
    link: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginLeft: "auto",
        fontSize: "var(--fontSizeBase200)",
    },
});

export interface StandaloneProjectPickerFluent9Props extends LocalizeContextProps {
    projects: IProjectData[];
    projectsLoading: boolean;
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    logger: Logger;
    officeWrapper: OfficeWrapper;
    autoSelectSuggested?: boolean;
    showValidationError?: boolean;
    selectedProject?: IProjectData;
    onProjectChange?: (project?: IProjectData) => void;
    onReloadProjects?: () => void | Promise<void>;
    onLoadingChange?: (isLoading: boolean) => void;
    /** Cache key to invalidate when the reload button is clicked. Defaults to fileTransferCacheName. */
    cacheKey?: ProjectsCacheKeys;
    /** Disables the picker and reload link regardless of loading state (e.g. while the parent form is submitting). */
    disabled?: boolean;
}

export const StandaloneProjectPickerFluent9 = withLocalize((props: StandaloneProjectPickerFluent9Props) => {
    const suggestedProjectsCount = 3;
    const myProjectsCount = 25;

    const styles = useStyles();

    const [suggestedProjects, setSuggestedProjects] = useState<IProjectData[]>([]);
    const [pickerResetToken, setPickerResetToken] = useState<number>(0);
    const [isReloading, setIsReloading] = useState(false);

    const isLoading = props.projectsLoading || isReloading;
    const isDisabled = isLoading || !!props.disabled;

    useEffect(() => {
        props.onLoadingChange?.(isLoading);
    }, [isLoading, props.onLoadingChange]);

    const selectedProjectDefaultHasRun = useRef(false);
    const prevMailboxItem = useRef<MailboxItem | null>(null);

    const currentMailboxItem = props.officeWrapper.currentContextItem;

    useEffect(() => {
        if (prevMailboxItem.current?.itemId !== currentMailboxItem?.itemId) {
            if (prevMailboxItem.current) props.onProjectChange?.(undefined);

            prevMailboxItem.current = currentMailboxItem;
            selectedProjectDefaultHasRun.current = false;

            setPickerResetToken((prev) => prev + 1);
            setSuggestedProjects([]);
        }

        const fetchSuggestions = async () => {
            if (props.projects.length < 1) return;

            try {
                const suggestionResults = await props.smartFilingManager.getSuggestedProjects(props.projects, false);
                if (suggestionResults.length) {
                    setSuggestedProjects(
                        suggestionResults.slice(0, suggestedProjectsCount).map((proj) => ({
                            name: proj.name,
                            number: proj.number,
                            displayName: proj.number ? `${proj.number} - ${proj.name}` : proj.name,
                            nrn: proj.nrn,
                            nixEnabled: proj.nixEnabled,
                            isDisabled: proj.isDisable,
                            code: proj.code,
                        }))
                    );
                }
            } catch (error) {
                props.logger.error(
                    "SuggestedProjectPickerComponent. There was an error retrieving suggested projects",
                    error
                );
            }
        };

        fetchSuggestions();
    }, [props.projects, currentMailboxItem]);

    const suggestedOptions = useMemo(() => {
        const label = props.translate("SHARED.SUGGESTED_PROJECTS.SUGGESTED_PROJECTS_TITLE").toString();
        return suggestedProjects.map((proj) => ({
            key: proj.nrn,
            label: proj.displayName,
            group: {
                key: "suggested",
                label: label,
            },
        }));
    }, [suggestedProjects]);

    const selectedPickerOption = useMemo(() => {
        if (props.selectedProject) {
            return {
                key: props.selectedProject.nrn,
                label: props.selectedProject.displayName,
            };
        }

        return undefined;
    }, [props.selectedProject]);

    const myOptions = useMemo(() => {
        const label = props.translate("SHARED.MY_PROJECTS").toString();

        return props.projects.map((proj) => ({
            key: proj.nrn,
            label: proj.displayName,
            group: {
                key: "my",
                label: label,
            },
        }));
    }, [props.projects]);

    const pickerOptions = useMemo(() => {
        return suggestedOptions.concat(
            myOptions.filter((myOpt) => !suggestedOptions.some((sOpt) => myOpt.key === sOpt.key))
        );
    }, [myOptions, suggestedOptions]);

    useEffect(() => {
        if (props.autoSelectSuggested ?? true) {
            if (props.selectedProject) {
                selectedProjectDefaultHasRun.current = true;
            }

            if (!selectedProjectDefaultHasRun.current && suggestedProjects.length > 0) {
                props.onProjectChange?.(suggestedProjects[0]);
                selectedProjectDefaultHasRun.current = true;
            }
        }
    }, [props.selectedProject, suggestedProjects]);

    const reloadProjectsOnClick = async () => {
        selectedProjectDefaultHasRun.current = false;
        setIsReloading(true);
        try {
            await props.invalidateCacheService.invalidateCache(props.cacheKey ?? DEFAULT_CACHE_KEY);
            setPickerResetToken((prev) => prev + 1);
            await props.onReloadProjects?.();
        } finally {
            setIsReloading(false);
        }
    };

    const onOptionSelect = (data?: TagData) =>
        props.onProjectChange?.(data ? props.projects.find((proj) => proj.nrn === data.key) : undefined);

    return (
        <div className={styles.root}>
            <SingleSelectTagPicker
                label={props.translate("SHARED.SUGGESTED_PROJECTS.LABEL").toString()}
                name="project"
                options={pickerOptions}
                selectedOption={selectedPickerOption}
                required
                disabled={isDisabled}
                limit={myProjectsCount + suggestedProjects.length}
                placeholder={
                    isLoading
                        ? props.translate("SEND_AND_FILE_NEW.LOADING_PROJECTS").toString()
                        : props.translate("SHARED.SUGGESTED_PROJECTS.PLACEHOLDER").toString()
                }
                noOptionsLabel={props.translate("SHARED.SUGGESTED_PROJECTS.NO_PROJECTS").toString()}
                limitExceededLabel={props.translate("SHARED.FIELD.PROJECT_LIMIT_EXCEEDED.LABEL").toString()}
                resetToken={pickerResetToken}
                onOptionSelect={onOptionSelect}
                validationMessage={
                    props.showValidationError ? props.translate("SHARED.FIELD.REQUIRED_ERROR").toString() : undefined
                }
                keepQueryOnClose
            />
            <div className={styles.container}>
                <Link
                    data-testid="reload-projects-button"
                    onClick={reloadProjectsOnClick}
                    className={styles.link}
                    disabled={isDisabled}
                >
                    <ArrowClockwise16Regular />
                    {props.translate("SEND_AND_FILE_NEW.RELOAD_PROJECTS")}
                </Link>
            </div>
        </div>
    );
});
